import { PropsWithChildren, createContext, useEffect, useRef } from 'react';
import { createStore, FrameworkReportsStore } from '../stores/framework-reportings.store';
import { useQuery } from '@tanstack/react-query';
import { coreService } from '../../../services/core/core-service';
import { format } from 'date-fns';

interface FrameworkReportsContextProps {
  store: FrameworkReportsStore;
}

export const FrameworkReportsContext = createContext<FrameworkReportsContextProps>({
  store: createStore({
    filterFrameworks: [],
    filterTypes: [],
    filterAccounts: [],
    filterCsms: [],
    filterFromDate: null,
    filterToDate: null,
    dateOption: '120d',
    page: 1,
    isLoading: false,
    isFetching: false,
  }),
});

export const FrameworkReportsProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const store = useRef(
    createStore({
      filterFrameworks: [],
      filterTypes: [],
      filterAccounts: [],
      filterCsms: [],
      filterFromDate: null,
      filterToDate: null,
      dateOption: '120d',
      page: 1,
      isLoading: false,
      isFetching: false,
    }),
  ).current;

  const {
    page,
    filterFrameworks,
    filterTypes,
    filterAccounts,
    filterCsms,
    filterFromDate,
    filterToDate,
    setFrameworkReportExecutions,
    setIsLoading,
    setRefetch,
  } = store();

  const { data, refetch, isFetching } = useQuery({
    queryKey: [
      'framework-report',
      page,
      filterFrameworks.join(','),
      filterTypes.join(','),
      filterAccounts.join(','),
      filterCsms.join(','),
      filterFromDate,
      filterToDate,
    ],
    queryFn: () =>
      coreService.getFrameworkReportingExecutions({
        frameworkIds: filterFrameworks.length ? filterFrameworks : null,
        types: filterTypes.length ? filterTypes : null,
        accountIds: filterAccounts.length ? filterAccounts : null,
        csmIds: filterCsms.length ? filterCsms : null,
        from: filterFromDate ? format(filterFromDate, 'yyyy-MM-dd') : null,
        to: filterToDate ? format(filterToDate, 'yyyy-MM-dd') : null,
        page,
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (data) {
      setFrameworkReportExecutions(data.items, data.metadata);
    }
  }, [data]);

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  useEffect(() => {
    setRefetch(refetch);
  }, [refetch]);

  return (
    <FrameworkReportsContext.Provider value={{ store }}>
      {children}
    </FrameworkReportsContext.Provider>
  );
};
